import * as Sentry from "@sentry/react";
import type { Action, ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import { configureStore as configureToolkitStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query/react";
import createSagaMiddleware from "redux-saga";

// -- Eventually remove these
import createStoriesMiddleware from "../../middleware/stories";
import createProjectStoriesMiddleware from "../../middleware/projectStories";
import createFolderStoriesMiddleware from "../../middleware/folderStories";
import { theirStoryAPI } from "../../api/theirStoryAPI";

import createCallMiddleware from "./call/middleware";
import type { RootState } from "./rootReducer";
import { rootReducer, routerMiddleware } from "./rootReducer";
import { rootSaga } from "./rootSaga";

export interface AppStoreOpts {
  // We use our Redux store in a few unit tests and having side-effects enabled
  // in those cases, makes things rather unpredictable so this option gives the
  // tests ability to disable saga in the redux store.
  skipSaga?: boolean;
}

export const configureStore = (opts?: AppStoreOpts) => {
  const thunkExtra = {} as ThunkExtraArg;

  const sagaMiddleware = opts?.skipSaga
    ? []
    : [
        createSagaMiddleware({
          onError(err) {
            console.error(err);
            Sentry.captureException(err);
          },
        }),
      ];

  const rootStore = configureToolkitStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      // https://redux-toolkit.js.org/usage/usage-with-typescript#correct-typings-for-the-dispatch-type
      getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
        thunk: { extraArgument: thunkExtra },
      }).concat(
        routerMiddleware as any,
        createCallMiddleware() as any,
        createStoriesMiddleware() as any,
        createProjectStoriesMiddleware() as any,
        createFolderStoriesMiddleware() as any,
        ...sagaMiddleware.map((m) => m as any),
        theirStoryAPI.middleware
      ),
  });
  setupListeners(rootStore.dispatch);

  thunkExtra.store = rootStore;

  if (!opts?.skipSaga) {
    sagaMiddleware[0].run(rootSaga);
  }

  return rootStore;
};

export type AppStore = ReturnType<typeof configureStore>;

export interface ThunkExtraArg {
  store: AppStore;
}

export type AppDispatch = ReturnType<typeof configureStore>["dispatch"];
export type AppActionCreator = Parameters<AppDispatch>[0];
export type DispatchTypeHelper = ThunkDispatch<RootState, ThunkExtraArg, Action>;
export type ActionTypeHelper<R = void> = ThunkAction<R, RootState, ThunkExtraArg, Action>;

declare module "react-redux" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface DefaultRootState extends RootState {}
}
